/**
 * useToast Hook
 *
 * L4-DISCORD-009: Convenience hook for toast notifications
 *
 * Provides a clean API for showing toast notifications:
 * - toast.success(message) - Shows green success toast
 * - toast.error(message) - Shows red error toast
 * - toast.info(message) - Shows info toast
 *
 * Usage:
 * ```tsx
 * const toast = useToast();
 * toast.success('Creature saved!');
 * toast.error('Failed to share');
 * ```
 */

import { useMemo } from 'react';
import { showNotification } from '../utils/notifications';

export interface ToastAPI {
  /**
   * Show a success toast notification
   * @param message - Message to display
   */
  success: (message: string) => void;

  /**
   * Show an error toast notification
   * @param message - Message to display
   */
  error: (message: string) => void;

  /**
   * Show an info toast notification
   * @param message - Message to display
   */
  info: (message: string) => void;
}

/**
 * Hook that provides a clean API for showing toast notifications.
 *
 * @returns Toast API object with success, error, and info methods
 */
export function useToast(): ToastAPI {
  const toast = useMemo<ToastAPI>(
    () => ({
      success: (message: string) => showNotification('success', message),
      error: (message: string) => showNotification('error', message),
      info: (message: string) => showNotification('info', message),
    }),
    []
  );

  return toast;
}

/**
 * Standalone toast object for non-React contexts.
 *
 * Use this when you need to show toasts outside of React components,
 * such as in utility functions or event handlers.
 *
 * Usage:
 * ```ts
 * import { toast } from './hooks/useToast';
 * toast.success('Operation complete!');
 * ```
 */
export const toast: ToastAPI = {
  success: (message: string) => showNotification('success', message),
  error: (message: string) => showNotification('error', message),
  info: (message: string) => showNotification('info', message),
};
